import {
  gearsAction,
  gearsLoader,
  gearsMeta,
  createGearsLoaderResponse,
  resolveGearsResult,
} from "@made-people/gears-remix";
import { useRouteChanged } from "@made-people/grease-remix";
import { useLoaderData } from "@remix-run/react";
import {
  CentraProductPage,
  StoryblokPage,
} from "@made-people/nestjs-bff-service";
import { DataFunctionArgs } from "@remix-run/node";
import { MmGtmRoot } from "../services/gtm/mm-gtm-root";

import { BreadCrumbsProvider } from "@made-people/grease-bread-crumbs";

import { CentraProductPageRoute } from "./centra-product-page-route";
import { NotFoundRoute } from "./not-found-route";
import { NotImplementedRoute } from "./not-implemented-route";
import { CheckoutIndexRoute } from "./checkout-index-route";
import { CheckoutFinalizeRoute } from "./checkout-finalize-route";
import { SearchPageRoute } from "./search-page-route";
import { StoryblokPageRoute } from "./storyblok-page-route";
import { UnimplementedRoute } from "../components/global/unimplemented-route";
import { type MmLoaderCustomExtraData } from "../utils/loader-custom-extra-data";

/**
 * The loader handles all communication with the BFF API and
 * returns the page response which we can use to render the
 * correct route component.
 */
export const loader = async (data: DataFunctionArgs) => {
  const result = await resolveGearsResult(data);

  // Add the custom extra data to the loader data.
  const customExtraData = {
    url: data.request.url,
    headers: [...data.request.headers.entries()] as [string, string][],
  };

  // Return the loader data with the custom extra data, so our routes
  // can access it.
  result.loaderData.customExtraData = customExtraData;

  return createGearsLoaderResponse(result);
};

/**
 * Meta title, opengraph tags etc. for the page using the BFF response
 */
export const meta = gearsMeta;

/**
 * Handle POST data for centra payment
 */
export const action = gearsAction;

/**
 * This is the component that selects the correct route component
 * depending on the BFF pageType response.
 */
function RouterInternal() {
  const {
    pageResponse: { page, frontendContext },
    customExtraData,
  } = useLoaderData<typeof loader>();

  // Not entirely sure of the typecasts here, remix turns the types
  // into recursively optional, losing the type information.
  switch (page.pageType) {
    case "checkoutIndex":
      return (
        <CheckoutIndexRoute
          baseUrl={frontendContext.baseUrl}
          frontendContext={frontendContext}
        />
      );
    case "checkoutFinalize":
      return <CheckoutFinalizeRoute />;
    case "storyblokPage":
      return (
        <BreadCrumbsProvider breadCrumbs={page.breadCrumbs}>
          <StoryblokPageRoute page={page as StoryblokPage} />
        </BreadCrumbsProvider>
      );
    case "centraProductPage":
      return (
        <CentraProductPageRoute
          frontendContext={frontendContext}
          page={page as CentraProductPage}
          customExtraData={customExtraData as MmLoaderCustomExtraData}
        />
      );
    case "searchPage":
      return <SearchPageRoute />;
    case "accountIndex":
      return <UnimplementedRoute />;
    case "accountLogin":
      return <UnimplementedRoute />;
    case "accountRegister":
      return <UnimplementedRoute />;
    case "forgotPassword":
      return <UnimplementedRoute />;
    case "notFound":
      return <NotFoundRoute />;
    default:
      if (process.env.NODE_ENV === "development") {
        return <NotImplementedRoute pageType={page.pageType} />;
      } else {
        // Render 404 in production for not implemented routes.
        return <NotFoundRoute />;
      }
  }
}

// This needs to be a separate component to prevent triggering re-render in the main
// component tree when the route changes.
const RouteChanged = () => {
  useRouteChanged();

  return null;
};

export default function Router() {
  const {
    pageResponse: { frontendContext },
  } = useLoaderData<typeof gearsLoader>();

  return (
    <>
      <MmGtmRoot frontendContext={frontendContext} />
      <RouteChanged />
      <RouterInternal />
    </>
  );
}
